import React, { useContext, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { Flex, Box } from 'rebass';
import { Controller, Scene } from 'react-scrollmagic';

import NewsArticleCard from '../../components/NewsArticleCard/NewsArticleCard';
import OldArtileCard from '../../components/NewsArticleCard/OldArticleCard';
import { PageContext } from '../../components/PageContext/PageContext';
import Typography from '../../components/Typography/Typography';

import { theme } from '../../styles/theme';
import parseNews from '../../utils/parseNews';
import isFadeEnabled from '../../utils/isFadeEnabled';
import { NewsSectionStoryblok } from '../../types/storyblok';
import classNames from 'classnames';

const NewsArticleBox = styled(Box)`
  ${(props) =>
    props.border &&
    css`
      @media (min-width: ${theme.breakpoint.sm}) {
        border-right: 1px solid ${theme.colors.darkGrey};

        &:nth-child(even) {
          border-right: 0;
        }

        &:last-child {
          border-right: 0;
        }
      }

      @media (min-width: ${theme.breakpoint.md}) {
        &:nth-child(even) {
          border-right: 1px solid ${theme.colors.darkGrey};
        }

        &:nth-child(3n + 0) {
          border-right: 0;
        }

        &:last-child {
          border-right: 0;
        }
      }
    `}
`;

const normalizeCategory = (category) => category.trim().replace(/\s/, '');

const NewsContainer = ({ news, itemsPerPage: itemsPerPageRaw, newCards }) => {
  const { data } = useContext(PageContext);
  const sectionEl = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = parseInt(itemsPerPageRaw) || 15;

  const updatePageIndex = (index) => {
    setCurrentPage(index);
    sectionEl.current.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const filterCategory = normalizeCategory(data.newsFilter || '');

  const filteredNews = news.filter(
    ({ category }) =>
      !filterCategory || filterCategory === normalizeCategory(category)
  );

  return (
    <Box ref={sectionEl} as="section" px={[3, 4]} py={[8, 9, 10]}>
      <Controller globalSceneOptions={{ triggerHook: 'onEnter' }}>
        <Scene classToggle="full-opacity">
          <Box
            as="div"
            className={classNames(
              'max-w-standard !mx-auto',
              isFadeEnabled() && 'zero-opacity'
            )}
          >
            <Box
              as="div"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-5"
            >
              {filteredNews
                .slice(
                  currentPage * itemsPerPage,
                  (currentPage + 1) * itemsPerPage
                )
                .map(({ uid: key, ...newsMeta }) => (
                  <NewsArticleBox key={key}>
                    {newCards ? (
                      <NewsArticleCard {...newsMeta} />
                    ) : (
                      <OldArtileCard {...newsMeta} />
                    )}
                  </NewsArticleBox>
                ))}
            </Box>
            <Box width={1} pt={[3, 4, 8]}>
              <Flex justifyContent="center" mx="auto">
                <Typography
                  inline
                  variant="link"
                  onClick={() => {
                    if (currentPage <= 0) return;
                    updatePageIndex(currentPage - 1);
                  }}
                >
                  Back
                </Typography>
                <Typography px={[3, 4, 5]}>
                  {`${currentPage * itemsPerPage + 1} to ${Math.min(
                    (currentPage + 1) * itemsPerPage,
                    filteredNews.length
                  )} of ${filteredNews.length}`}
                </Typography>
                <Typography
                  inline
                  variant="link"
                  onClick={() => {
                    if ((currentPage + 1) * itemsPerPage >= filteredNews.length)
                      return;
                    updatePageIndex(currentPage + 1);
                  }}
                >
                  Next
                </Typography>
              </Flex>
            </Box>
          </Box>
        </Scene>
      </Controller>
    </Box>
  );
};

const NewsSection = (props: NewsSectionStoryblok) => {
  const data = useStaticQuery(graphql`
    query {
      stories: allStoryblokEntry(
        filter: {
          field_title_string: { ne: null }
          full_slug: { regex: "/^articles//" }
          field_component: { in: ["NewsItem", "page"] }
        }
        sort: { field_datePublished_string: DESC }
      ) {
        nodes {
          field_component
          field_title_string
          field_description_string
          field_category_string
          field_datePublished_string
          field_link_string
          field_quote_string
          full_slug
          uuid
        }
      }
    }
  `);
  const fitsLocation = ({ full_slug }) =>
    props.location ? full_slug.startsWith(props.location) : true;
  const hasLink = ({ link }) => Boolean(link);
  const nodes = data.stories.nodes
    .filter(fitsLocation)
    .map((story) => parseNews(story))
    .filter(hasLink);

  return <NewsContainer news={nodes} {...props} />;
};

export default NewsSection;
